import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Eye, EyeOff, Megaphone, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { create } from "zustand";

import { api, ApiError } from "../../api/client";
import type { Announcement, AnnouncementLevel, AnnouncementStatus, AnnouncementSummary } from "../../api/types";
import { attentionSelector, designate } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Dialog } from "../../components/Dialog";
import { Spinner } from "../../components/EmptyState";
import { Select } from "../../components/Select";
import { PropRow } from "../../components/panel";
import { motion, Presence } from "../../components/presence";
import { Button, Field, IconButton, inputClass, Segmented, textareaClass } from "../../components/ui";
import { Waiting } from "../../components/Waiting";
import { lang, t } from "../../i18n/i18n";
import { plural } from "../../plural";
import { AnnouncementBanner } from "../AnnouncementBanner";
import {
  fromParisInputs,
  isoMinute,
  phaseAt,
  slotText,
  templateMessages,
  toParisInputs,
} from "../announcement";
import { isPreprod, useEnvironmentValue } from "../environment";
import { blockerText, formatParis, formatParisTime, missingField, missingText, PASSWORD_MAX, requestErrorText } from "./promotion";

/** Fenêtre « Annoncer une maintenance en production » (registre F40, docs/11 §11.10), à côté de « Promouvoir… » sur
 *  l'accueil de la préproduction. Niveau (information, interruption de service), début (date et heure de Paris,
 *  écrit en clair), fin par durée estimée ou par heure, texte en français et en anglais pré-rempli d'un modèle qui
 *  suit les dates tant qu'il n'est pas retouché (« Reprendre le modèle »), aperçu du bandeau tel que les utilisateurs
 *  le verront, puis le mot de passe de promotion, le même que pour « Promouvoir ». Une annonce publiée se modifie
 *  (même fenêtre, pré-remplie) ou se retire. Tant que le service de promotion n'a pas vérifié le mot de passe, la
 *  fenêtre reste sur la saisie (« Vérification du mot de passe… ») ; refusée, elle y revient avec le motif. */

export const ANNOUNCEMENT_KEY = ["preprod", "announcement"] as const;
export const POLL_MS = 1000;
const MESSAGE_MAX = 500;
const DURATION_MIN = 5;
const DURATION_MAX = 72 * 60;
/** Durées proposées (minutes). */
export const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720, 1440] as const;

const useDialog = create<{ open: boolean }>(() => ({ open: false }));
export const openMaintenanceDialog = () => useDialog.setState({ open: true });

/** Durée en clair (« 45 min », « 1 h 30 », « 2 h »). */
export function durationText(minutes: number): string {
  if (minutes < 60) return t("{n} min", { n: minutes });
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? t("{h} h {m}", { h, m: String(m).padStart(2, "0") }) : t("{h} h", { h });
}

/** Fenêtre, à poser à la racine de l'accueil (préproduction seulement). */
export function MaintenanceDialogHost() {
  const preprod = useEnvironmentValue(isPreprod);
  const open = useDialog((s) => s.open);
  const summary = useQuery<AnnouncementSummary>({ queryKey: ANNOUNCEMENT_KEY, queryFn: api.preprod.announcement, enabled: false });
  useEffect(() => () => useDialog.setState({ open: false }), []);
  const shown = preprod && open && Boolean(summary.data);
  return (
    <Presence show={shown}>
      {shown && summary.data && <MaintenanceDialog summary={summary.data} onClose={() => useDialog.setState({ open: false })} />}
    </Presence>
  );
}

/** Ligne de l'encadré « Promotion en production » : annonce publiée (créneau, niveau) ou aucune. */
export function MaintenanceLine() {
  const summary = useQuery<AnnouncementSummary>({ queryKey: ANNOUNCEMENT_KEY, queryFn: api.preprod.announcement, refetchOnWindowFocus: true });
  const current = summary.data?.current ?? null;
  return (
    <PropRow label={t("Annonce de maintenance")}>
      {summary.isError
        ? t("Illisible")
        : !summary.data
          ? <Waiting>{t("Lecture…")}</Waiting>
          : current
            ? `${current.level === "outage" ? t("Interruption de service") : t("Information")}, ${slotText(current)}`
            : t("Aucune")}
    </PropRow>
  );
}

/** Bouton de l'encadré, à côté de « Promouvoir… ». */
export function MaintenanceButton() {
  const summary = useQuery<AnnouncementSummary>({ queryKey: ANNOUNCEMENT_KEY, queryFn: api.preprod.announcement, refetchOnWindowFocus: true });
  return (
    <Button variant="secondary" data-attention="maintenance-open" disabled={!summary.data} onClick={openMaintenanceDialog}>
      <Megaphone size={14} /> {summary.data?.current ? t("Annonce de maintenance…") : t("Annoncer une maintenance…")}
    </Button>
  );
}

export interface Draft {
  level: AnnouncementLevel;
  date: string;
  time: string;
  endMode: "duration" | "end";
  duration: number;
  endDate: string;
  endTime: string;
  messageFr: string;
  messageEn: string;
  /** Texte retouché à la main : il ne suit plus les dates. */
  edited: boolean;
}

/** Saisie de départ : l'annonce publiée (à modifier), sinon demain de 22:00 à 23:00, heure de Paris. */
export function initialDraft(current: Announcement | null, now: number): Draft {
  if (current) {
    const starts = Date.parse(current.starts_at);
    const ends = Date.parse(current.ends_at);
    const s = toParisInputs(starts);
    const e = toParisInputs(ends);
    const minutes = Math.round((ends - starts) / 60_000);
    const listed = (DURATIONS as readonly number[]).includes(minutes);
    return {
      level: current.level,
      date: s.date,
      time: s.time,
      endMode: listed ? "duration" : "end",
      duration: listed ? minutes : 60,
      endDate: e.date,
      endTime: e.time,
      messageFr: current.message.fr,
      messageEn: current.message.en,
      edited: true,
    };
  }
  const tomorrow = toParisInputs(now + 86_400_000);
  const starts = fromParisInputs(tomorrow.date, "22:00") ?? now + 86_400_000;
  const messages = templateMessages("outage", starts, starts + 3_600_000);
  return {
    level: "outage",
    date: tomorrow.date,
    time: "22:00",
    endMode: "duration",
    duration: 60,
    endDate: tomorrow.date,
    endTime: "23:00",
    messageFr: messages.fr,
    messageEn: messages.en,
    edited: false,
  };
}

/** Début et fin saisis (ms), null s'ils sont incomplets. */
export function draftTimes(d: Draft): { starts: number | null; ends: number | null } {
  const starts = fromParisInputs(d.date, d.time);
  const ends = d.endMode === "duration" ? (starts === null ? null : starts + d.duration * 60_000) : fromParisInputs(d.endDate, d.endTime);
  return { starts, ends };
}

/** Textes de la saisie : le modèle tant qu'ils n'ont pas été retouchés. */
export function draftMessages(d: Draft): { fr: string; en: string } {
  const { starts, ends } = draftTimes(d);
  if (d.edited || starts === null || ends === null || ends <= starts) return { fr: d.messageFr, en: d.messageEn };
  return templateMessages(d.level, starts, ends);
}

export type DraftField = "start" | "end" | "fr" | "en";

/** Premier défaut de la saisie (champ et explication), null si elle peut partir. Mêmes règles que le serveur. */
export function draftProblem(d: Draft, now: number): { field: DraftField; text: string } | null {
  const { starts, ends } = draftTimes(d);
  if (starts === null) return { field: "start", text: t("Indiquez la date et l'heure du début.") };
  if (ends === null) return { field: "end", text: t("Indiquez la date et l'heure de la fin.") };
  const minutes = (ends - starts) / 60_000;
  if (minutes < DURATION_MIN || minutes > DURATION_MAX)
    return { field: "end", text: t("La maintenance dure de 5 minutes à 72 heures : vérifiez la fin.") };
  if (ends <= now + 60_000) return { field: "end", text: t("Cette maintenance serait déjà finie : vérifiez les dates.") };
  if (starts > now + 92 * 86_400_000) return { field: "start", text: t("Le début est à plus de trois mois : vérifiez la date.") };
  const messages = draftMessages(d);
  const tidy = (s: string) => s.replace(/\s+/g, " ").trim();
  if (!tidy(messages.fr) || [...tidy(messages.fr)].length > MESSAGE_MAX)
    return { field: "fr", text: t("Le texte en français compte de 1 à {n} signes.", { n: MESSAGE_MAX }) };
  if (!tidy(messages.en) || [...tidy(messages.en)].length > MESSAGE_MAX)
    return { field: "en", text: t("Le texte en anglais compte de 1 à {n} signes.", { n: MESSAGE_MAX }) };
  return null;
}

/** Annonce telle qu'elle sera publiée (aperçu). */
export function draftAnnouncement(d: Draft): Announcement | null {
  const { starts, ends } = draftTimes(d);
  if (starts === null || ends === null || ends <= starts) return null;
  const messages = draftMessages(d);
  return {
    id: "0".repeat(32),
    level: d.level,
    starts_at: isoMinute(starts),
    ends_at: isoMinute(ends),
    message: { fr: messages.fr.trim() || "…", en: messages.en.trim() || "…" },
  };
}

/** Motif d'un refus du service de promotion, en clair. */
export function refusalText(status: AnnouncementStatus): string {
  switch (status.reason) {
    case "code":
      return typeof status.remaining === "number" && status.remaining > 0
        ? t("Mot de passe de promotion incorrect : encore {tries} avant un blocage d'une heure.", {
            tries: plural(status.remaining, t("essai"), t("essais")),
          })
        : t("Mot de passe de promotion incorrect : vérifiez-le, puis recommencez.");
    case "locked":
      return blockerText("locked", status.locked_until);
    case "not_configured":
      return blockerText("not_configured");
    case "expired":
      return t("La demande a mis trop de temps à parvenir au service de promotion : recommencez.");
    case "dates":
      return t("Les dates ne conviennent pas (maintenance déjà finie, durée de plus de 72 heures ou début à plus de trois mois) : vérifiez-les.");
    case "invalid":
      return t("Demande illisible : rechargez la page, puis recommencez.");
    default:
      return t("L'annonce n'a pas pu être publiée pour une raison imprévue : réessayez dans un instant.");
  }
}

/** Refus de l'API (avant le service de promotion) : texte et champ à désigner. */
export function announceErrorText(error: unknown): { text: string; field?: DraftField | "password" } {
  if (error instanceof ApiError) {
    const field = typeof error.detail?.field === "string" ? error.detail.field : null;
    if (error.code === "ANNOUNCEMENT_INVALID") {
      const map: Record<string, DraftField> = { starts_at: "start", ends_at: "end", message_fr: "fr", message_en: "en" };
      return { text: t("Vérifiez les dates et les textes de l'annonce, puis recommencez."), field: field ? map[field] : undefined };
    }
    if (error.code === "PROMOTION_RUNNING")
      return { text: t("Une promotion est en cours : annoncez la maintenance une fois la promotion finie.") };
    if (error.status === 429) return { text: t("Trop d'annonces envoyées à la suite : patientez une minute.") };
  }
  return requestErrorText(error);
}

const designateField = (field: DraftField | "password") => designate(attentionSelector(`annonce-${field}`), "field");

export function MaintenanceDialog({ summary, onClose }: { summary: AnnouncementSummary; onClose: () => void }) {
  const queryClient = useQueryClient();
  const current = summary.current;
  const [draft, setDraft] = useState<Draft>(() => initialDraft(current, Date.now()));
  const [password, setPassword] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const [status, setStatus] = useState<AnnouncementStatus | null>(null);
  const [stalled, setStalled] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ANNOUNCEMENT_KEY });
  const set = (patch: Partial<Draft>) => {
    setHint(null);
    setDraft((d) => ({ ...d, ...patch }));
  };

  const send = useMutation({
    mutationFn: (action: "publish" | "withdraw") => {
      const { starts, ends } = draftTimes(draft);
      const messages = draftMessages(draft);
      return api.preprod.announce(
        action === "withdraw"
          ? { password, action }
          : {
              password,
              action,
              level: draft.level,
              starts_at: isoMinute(starts ?? 0),
              ends_at: isoMinute(ends ?? 0),
              message_fr: messages.fr,
              message_en: messages.en,
            },
      );
    },
    onSuccess: (reply) => {
      setPassword("");
      setStalled(false);
      setStatus(reply.status);
    },
    onError: (error) => {
      setPassword("");
      const field = announceErrorText(error).field;
      if (field) designateField(field);
    },
  });

  // Suivi de la demande jusqu'à l'issue publiée par le service de promotion.
  const requestId = status?.state === "pending" ? status.request_id : null;
  useEffect(() => {
    if (!requestId) return;
    let stop = false;
    const timer = window.setTimeout(async () => {
      try {
        const reply = await api.preprod.announcementStatus(requestId);
        if (stop) return;
        setStalled(reply.stalled);
        setStatus(reply.status ? reply.status : (s) => (s ? { ...s } : s));
      } catch {
        if (!stop) setStatus((s) => (s ? { ...s } : s));
      }
    }, POLL_MS);
    return () => {
      stop = true;
      window.clearTimeout(timer);
    };
  }, [requestId, status]);
  const final = status !== null && status.state !== "pending";
  useEffect(() => {
    if (!final) return;
    refresh();
    if (status?.state === "refused" && status.reason === "code") designateField("password");
  }, [final]);

  const checking = send.isPending || status?.state === "pending";
  const done = status?.state === "published" || status?.state === "withdrawn";
  const lockedUntil = summary.locked_until;
  const blocked = !summary.configured || Boolean(lockedUntil) || summary.promotion_running;
  const blockedText = !summary.configured
    ? blockerText("not_configured")
    : lockedUntil
      ? blockerText("locked", lockedUntil)
      : t("Une promotion est en cours : annoncez la maintenance une fois la promotion finie.");
  const problem = draftProblem(draft, now);
  const preview = useMemo(() => draftAnnouncement(draft), [draft]);
  const messages = draftMessages(draft);
  const { starts, ends } = draftTimes(draft);

  const submit = (action: "publish" | "withdraw") => {
    if (checking) return;
    if (blocked) {
      designate(attentionSelector("annonce-blocker"), "ring");
      return;
    }
    if (action === "publish" && problem) {
      setHint(problem.text);
      designateField(problem.field);
      return;
    }
    const missing = missingField(password);
    if (missing) {
      setHint(missingText(missing));
      designateField("password");
      return;
    }
    setHint(null);
    setRevealed(false);
    setStatus(null);
    send.mutate(action);
  };
  const reveal = revealed ? t("Masquer le mot de passe") : t("Afficher le mot de passe");
  const otherLang = lang() === "en" ? messages.fr : messages.en;

  return (
    <Dialog
      title={t("Annonce de maintenance en production")}
      subtitle={t("Les utilisateurs de la production voient un bandeau en haut de l'écran, dans leur langue.")}
      width={620}
      onClose={onClose}
      dirty={!done && password !== ""}
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>
            {t("Fermer")}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {checking ? t("Fermer") : t("Annuler")}
            </Button>
            {current && (
              <Button
                variant="danger-ghost"
                data-annonce-withdraw
                disabled={checking}
                blocked={blocked || missingField(password) !== null}
                onBlocked={() => submit("withdraw")}
                onClick={() => submit("withdraw")}
              >
                <Trash2 size={14} /> {t("Retirer l'annonce")}
              </Button>
            )}
            <Button
              data-annonce-submit
              disabled={checking}
              blocked={blocked || problem !== null || missingField(password) !== null}
              onBlocked={() => submit("publish")}
              onClick={() => submit("publish")}
            >
              {checking ? <Spinner decorative /> : <Megaphone size={14} />}{" "}
              {checking ? (
                <Waiting>{t("Vérification du mot de passe…")}</Waiting>
              ) : current ? (
                t("Mettre à jour l'annonce")
              ) : (
                t("Publier l'annonce")
              )}
            </Button>
          </>
        )
      }
    >
      {done && status ? (
        <div className={clsx("px-4 py-4", motion("fade"))}>
          <Callout tone="ok" title={status.state === "published" ? t("Annonce publiée") : t("Annonce retirée")}>
            {status.state === "published"
              ? t("Les onglets ouverts en production l'affichent dans les 5 minutes, les autres dès leur ouverture. Elle disparaît d'elle-même à la fin de la maintenance.")
              : t("Le bandeau disparaît des onglets ouverts en production dans les 5 minutes.")}
          </Callout>
        </div>
      ) : (
        <div className="space-y-4 px-4 py-4">
          {current && (
            <div className="rounded-lg border border-line px-3">
              <PropRow label={t("Annonce publiée")}>
                {`${current.level === "outage" ? t("Interruption de service") : t("Information")}, ${slotText(current)}`}
              </PropRow>
              {current.published_at && <PropRow label={t("Publiée le")}>{formatParis(current.published_at)}</PropRow>}
            </div>
          )}
          <Segmented<AnnouncementLevel>
            label={t("Niveau")}
            value={draft.level}
            onChange={(level) => set({ level })}
            options={[
              { value: "info", label: t("Information") },
              { value: "outage", label: t("Interruption de service") },
            ]}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t("Début (heure de Paris)")}>
              <span className="flex gap-2" data-attention="annonce-start">
                <input type="date" className={inputClass} value={draft.date} onChange={(e) => set({ date: e.target.value })} disabled={checking} />
                <input type="time" className={clsx(inputClass, "w-28 shrink-0")} value={draft.time} onChange={(e) => set({ time: e.target.value })} disabled={checking} />
              </span>
            </Field>
            <div data-attention="annonce-end">
              <Segmented<"duration" | "end">
                size="sm"
                ariaLabel={t("Fin")}
                value={draft.endMode}
                onChange={(endMode) => {
                  const { ends: at } = draftTimes(draft);
                  const e = at !== null ? toParisInputs(at) : { date: draft.date, time: draft.time };
                  set({ endMode, endDate: e.date, endTime: e.time });
                }}
                options={[
                  { value: "duration", label: t("Durée estimée") },
                  { value: "end", label: t("Heure de fin") },
                ]}
                className="mb-1"
              />
              {draft.endMode === "duration" ? (
                <Select
                  aria-label={t("Durée estimée")}
                  value={String(draft.duration)}
                  onChange={(e) => set({ duration: Number(e.target.value) })}
                  disabled={checking}
                >
                  {DURATIONS.map((m) => (
                    <option key={m} value={String(m)}>
                      {durationText(m)}
                    </option>
                  ))}
                </Select>
              ) : (
                <span className="flex gap-2">
                  <input type="date" aria-label={t("Date de fin")} className={inputClass} value={draft.endDate} onChange={(e) => set({ endDate: e.target.value })} disabled={checking} />
                  <input type="time" aria-label={t("Heure de fin")} className={clsx(inputClass, "w-28 shrink-0")} value={draft.endTime} onChange={(e) => set({ endTime: e.target.value })} disabled={checking} />
                </span>
              )}
            </div>
          </div>
          <p className="-mt-1 text-meta leading-snug text-ink-muted">
            {starts !== null && ends !== null && ends > starts
              ? t("Maintenance {slot}, heure de Paris (durée {duration}).", { slot: slotText({ starts_at: isoMinute(starts), ends_at: isoMinute(ends) }), duration: durationText(Math.round((ends - starts) / 60_000)) })
              : t("Indiquez le début et la fin.")}
          </p>
          <Field label={t("Texte en français")}>
            <textarea
              data-attention="annonce-fr"
              className={textareaClass}
              rows={3}
              maxLength={MESSAGE_MAX}
              value={messages.fr}
              onChange={(e) => set({ messageFr: e.target.value, messageEn: messages.en, edited: true })}
              disabled={checking}
            />
          </Field>
          <Field label={t("Texte en anglais")}>
            <textarea
              data-attention="annonce-en"
              className={textareaClass}
              rows={3}
              maxLength={MESSAGE_MAX}
              value={messages.en}
              onChange={(e) => set({ messageEn: e.target.value, messageFr: messages.fr, edited: true })}
              disabled={checking}
            />
          </Field>
          <Presence show={draft.edited}>
            {draft.edited && (
              <div className={clsx("-mt-2 flex justify-end", motion("fade"))}>
                <Button variant="ghost" size="sm" onClick={() => set({ edited: false })} disabled={checking}>
                  <RotateCcw size={14} /> {t("Reprendre le modèle")}
                </Button>
              </div>
            )}
          </Presence>
          <section aria-label={t("Aperçu")}>
            <span className="mb-1 block text-ui font-medium text-ink-soft">{t("Aperçu du bandeau")}</span>
            {preview ? (
              <AnnouncementBanner announcement={preview} phase={phaseAt(preview, now) ?? "upcoming"} now={now} preview />
            ) : (
              <p className="flex min-h-8 items-center text-ui text-ink-muted">{t("Indiquez le début et la fin.")}</p>
            )}
            <p className="mt-1.5 text-meta leading-snug break-words text-ink-muted">
              {lang() === "en" ? t("Montré en français : {text}", { text: otherLang }) : t("Montré en anglais : {text}", { text: otherLang })}
            </p>
          </section>
          <Field
            label={t("Mot de passe de promotion")}
            hint={t("Le même que pour « Promouvoir ». Cinq erreurs en une heure bloquent pendant une heure les promotions, les annonces et le Terminal.")}
          >
            <span className="relative block">
              <input
                type={revealed ? "text" : "password"}
                data-attention="annonce-password"
                className={clsx(inputClass, "pr-9")}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setHint(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    submit("publish");
                  }
                }}
                autoComplete="one-time-code"
                data-1p-ignore
                data-lpignore="true"
                autoCapitalize="none"
                autoCorrect="off"
                maxLength={PASSWORD_MAX}
                spellCheck={false}
                disabled={checking || blocked}
              />
              <IconButton
                size="sm"
                className="absolute top-1/2 right-0.5 -translate-y-1/2"
                onClick={(e) => {
                  e.preventDefault();
                  setRevealed((r) => !r);
                }}
                title={reveal}
                aria-label={reveal}
                aria-pressed={revealed}
                disabled={checking || blocked}
              >
                <span key={revealed ? "shown" : "hidden"} className={clsx("flex", motion("fade"))}>
                  {revealed ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
                </span>
              </IconButton>
            </span>
          </Field>
        </div>
      )}
      <div className="flex flex-col gap-3 px-4 pb-4 empty:hidden">
        <Presence show={blocked && !done}>
          {blocked && !done && (
            <div data-attention="annonce-blocker" className="rounded-lg">
              <Callout tone={lockedUntil ? "warn" : "info"}>{blockedText}</Callout>
            </div>
          )}
        </Presence>
        <Presence show={Boolean(checking)}>
          {checking && (
            <div role="status">
              <Callout tone={stalled ? "warn" : "info"}>
                {stalled
                  ? t("Le service de promotion ne répond pas : la demande attend d'être prise en charge. Rien n'a changé en production.")
                  : t("Vérification du mot de passe par le service de promotion… Rien n'a changé en production.")}
              </Callout>
            </div>
          )}
        </Presence>
        <Presence show={status?.state === "refused"}>
          {status?.state === "refused" && (
            <Callout tone="danger" title={t("Annonce refusée")}>
              {refusalText(status)} {t("Rien n'a changé en production.")}
            </Callout>
          )}
        </Presence>
        <Presence show={send.isError && !checking}>
          {send.isError && !checking && <Callout tone="danger">{announceErrorText(send.error).text}</Callout>}
        </Presence>
        <Presence show={Boolean(hint) && !send.isError}>
          {hint && !send.isError && <Callout tone="info">{hint}</Callout>}
        </Presence>
        {!done && !checking && starts !== null && starts > now && (
          <p className="text-meta leading-snug text-ink-muted">
            {t("Le bandeau s'affiche dès la publication ; fermé par un utilisateur, il revient 15 minutes avant le début ({time}).", { time: formatParisTime(isoMinute(starts - 15 * 60_000)) })}
          </p>
        )}
      </div>
    </Dialog>
  );
}
